import { useState } from "react";
import { Plus, Trash2, RotateCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { useAuthStore } from "@/store/auth";
import { formatAmount } from "@/utils/formatAmount";

import {
  useCreateSignatureType,
  useSignatureTypes,
  useSoftDeleteAndReplaceSignatureType,
  useSoftDeleteSignatureType,
  useUpdateSignatureType,
} from "../hooks/useEmployeeAgreements";
import type { EmployeeSignatureType } from "../types";

interface Props {
  agreementId: string;
  readOnly?: boolean;
}

export function SignatureTypesTable({ agreementId, readOnly = false }: Props) {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const isCeo = !!useAuthStore((s) => s.user)?.is_ceo;
  const { data: types = [], isLoading } = useSignatureTypes(
    agreementId,
    includeDeleted,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [replaceFor, setReplaceFor] = useState<EmployeeSignatureType | null>(
    null,
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-mono uppercase tracking-wider text-[var(--color-text-secondary)]">
            Typy sprawdzenia i podpisu
          </h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Lista dokumentów, które pracownik może podpisywać, wraz ze stawkami.
          </p>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0">
          <label className="text-xs flex items-center gap-2 text-[var(--color-text-muted)] cursor-pointer">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="rounded-sm"
            />
            Pokaż usunięte
          </label>
          {!readOnly && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus size={14} /> Dodaj
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="text-sm text-[var(--color-text-muted)] py-8 text-center border border-dashed border-[var(--color-border)]">
          Ładowanie…
        </div>
      ) : types.length === 0 ? (
        <div className="text-sm text-[var(--color-text-muted)] py-10 text-center border border-dashed border-[var(--color-border)]">
          Brak typów podpisu — dodaj pierwszy przyciskiem „Dodaj".
        </div>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col gap-2">
            {types.map((t) => (
              <div
                key={t.id}
                className={`border border-[var(--color-border)] bg-white dark:bg-gray-800 px-3 py-2.5 flex flex-col gap-2${
                  t.is_deleted ? " opacity-50" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm break-words">{t.name}</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      {t.unit}
                      {isCeo && (
                        <>
                          {" · "}
                          {t.is_free ? (
                            <span className="italic">darmowy</span>
                          ) : (
                            <span className="font-mono">
                              {formatAmount(t.rate)} PLN
                            </span>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                  {t.is_deleted ? (
                    <Badge color="red">Usunięty</Badge>
                  ) : t.is_in_use ? (
                    <Badge color="blue">W użyciu</Badge>
                  ) : (
                    <Badge color="gray">Nieużyty</Badge>
                  )}
                </div>
                {!readOnly && !t.is_deleted && (
                  <div className="flex gap-1 justify-end pt-1 border-t border-[var(--color-border)]">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setReplaceFor(t)}
                      title="Zastąp nowym typem"
                    >
                      <RotateCw size={14} /> Zastąp
                    </Button>
                    <SoftDeleteButton
                      agreementId={agreementId}
                      typeId={t.id}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block border border-[var(--color-border)] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg-elevated)]">
                <tr className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
                  <th className="text-left px-4 py-3 font-mono">Nazwa</th>
                  <th className="text-left px-4 py-3 font-mono">Jednostka</th>
                  {isCeo && (
                    <th className="text-right px-4 py-3 font-mono">Stawka</th>
                  )}
                  <th className="text-left px-4 py-3 font-mono">Status</th>
                  {!readOnly && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {types.map((t) => (
                  <tr
                    key={t.id}
                    className={
                      t.is_deleted
                        ? "opacity-50"
                        : "hover:bg-[var(--color-bg-hover)] transition-colors"
                    }
                  >
                    <td className="px-4 py-3 font-medium">{t.name}</td>
                    <td className="px-4 py-3 text-[var(--color-text-muted)]">
                      {t.unit}
                    </td>
                    {isCeo && (
                      <td className="px-4 py-3 text-right font-mono">
                        {t.is_free ? (
                          <span className="text-[var(--color-text-muted)] italic">
                            darmowy
                          </span>
                        ) : (
                          <span>{formatAmount(t.rate)} PLN</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {t.is_deleted ? (
                        <Badge color="red">Usunięty</Badge>
                      ) : t.is_in_use ? (
                        <Badge color="blue">W użyciu</Badge>
                      ) : (
                        <Badge color="gray">Nieużyty</Badge>
                      )}
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-3 text-right">
                        {!t.is_deleted && (
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setReplaceFor(t)}
                              title="Zastąp nowym typem (wycofuje stary, tworzy nowy)"
                            >
                              <RotateCw size={14} />
                            </Button>
                            <SoftDeleteButton
                              agreementId={agreementId}
                              typeId={t.id}
                            />
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <SignatureTypeForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        agreementId={agreementId}
      />
      {replaceFor && (
        <ReplaceTypeDialog
          open={!!replaceFor}
          onClose={() => setReplaceFor(null)}
          source={replaceFor}
          agreementId={agreementId}
        />
      )}
    </div>
  );
}

function SoftDeleteButton({
  agreementId,
  typeId,
}: {
  agreementId: string;
  typeId: string;
}) {
  const { mutate, isPending } = useSoftDeleteSignatureType(agreementId);
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={isPending}
      onClick={() => {
        if (confirm("Usunąć ten typ podpisu? Tej akcji nie można cofnąć.")) {
          mutate(typeId);
        }
      }}
    >
      <Trash2 size={14} />
    </Button>
  );
}

interface FormProps {
  open: boolean;
  onClose: () => void;
  agreementId: string;
}

function SignatureTypeForm({ open, onClose, agreementId }: FormProps) {
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("szt.");
  const [isFree, setIsFree] = useState(false);
  const [rate, setRate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useCreateSignatureType(agreementId);

  function submit() {
    setError(null);
    mutate(
      {
        agreement: agreementId,
        name,
        unit,
        is_free: isFree,
        rate: isFree ? null : rate,
      },
      {
        onSuccess: () => {
          onClose();
          setName("");
          setUnit("szt.");
          setIsFree(false);
          setRate("");
        },
        onError: () => setError("Nie udało się utworzyć typu."),
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nowy typ sprawdzenia i podpisu"
      size="sm"
    >
      <div className="flex flex-col gap-3">
        <Input
          label="Nazwa"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Jednostka"
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isFree}
            onChange={(e) => setIsFree(e.target.checked)}
          />
          Podpis darmowy (bez stawki)
        </label>
        {!isFree && (
          <Input
            label="Stawka [PLN]"
            type="number"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        )}
        {error && <div className="text-sm text-red-500">{error}</div>}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            disabled={isPending || !name.trim() || (!isFree && !rate)}
            onClick={submit}
          >
            Utwórz
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface ReplaceProps {
  open: boolean;
  onClose: () => void;
  source: EmployeeSignatureType;
  agreementId: string;
}

function ReplaceTypeDialog({
  open,
  onClose,
  source,
  agreementId,
}: ReplaceProps) {
  const [name, setName] = useState(source.name);
  const [unit, setUnit] = useState(source.unit);
  const [isFree, setIsFree] = useState(source.is_free);
  const [rate, setRate] = useState(source.rate ?? "");
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } =
    useSoftDeleteAndReplaceSignatureType(agreementId);

  function submit() {
    setError(null);
    mutate(
      {
        id: source.id,
        payload: { name, unit, is_free: isFree, rate: isFree ? null : rate },
      },
      {
        onSuccess: onClose,
        onError: () => setError("Nie udało się zastąpić typu."),
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Zastąp typ podpisu nowym"
      size="sm"
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-[var(--color-text-muted)]">
          Stary typ pozostanie w historii (oznaczony jako usunięty). Nowy typ
          będzie obowiązywał dla przyszłych klastrów.
        </p>
        <Input
          label="Nazwa"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Jednostka"
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isFree}
            onChange={(e) => setIsFree(e.target.checked)}
          />
          Podpis darmowy
        </label>
        {!isFree && (
          <Input
            label="Nowa stawka [PLN]"
            type="number"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        )}
        {error && <div className="text-sm text-red-500">{error}</div>}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={isPending} onClick={submit}>
            Zastąp
          </Button>
        </div>
      </div>
    </Modal>
  );
}
